import { formatLocationRelativeTime } from "../../../utils/locationLocale";
import { t } from "i18next";
import type { ILocationProps } from '../../../services/Location/types';

const RECENT_LOCATION_MS = 60_000;
const DELAYED_LOCATION_MS = 5 * 60_000;

export type LocationTelemetryStatus =
  | 'recent'
  | 'delayed'
  | 'stale'
  | 'missing';

export function hasValidLocation(location: ILocationProps) {
  return (
    location.hasLocation === true &&
    typeof location.latitude === 'number' &&
    Number.isFinite(location.latitude) &&
    typeof location.longitude === 'number' &&
    Number.isFinite(location.longitude)
  );
}

export function getLocationTelemetryStatus(
  location: ILocationProps,
  now = Date.now(),
): LocationTelemetryStatus {
  if (!hasValidLocation(location) || !location.timestamp) return 'missing';

  const timestamp = new Date(location.timestamp).getTime();
  if (!Number.isFinite(timestamp)) return 'missing';

  const age = Math.max(0, now - timestamp);
  if (age <= RECENT_LOCATION_MS) return 'recent';
  if (age <= DELAYED_LOCATION_MS) return 'delayed';
  return 'stale';
}

export function formatLocationUpdatedAt(
  timestamp?: string | null,
  now = Date.now(),
) {
  const time = formatLocationRelativeTime(timestamp, now);
  return time === null
    ? t("locationTransport.noUpdate")
    : t("locationTransport.updatedAgo", { time });
}

export function getOperationalStatus(location: ILocationProps) {
  const isMobileDriver = location.source === 'mobile-driver';

  if (location.isOnline === true) {
    return {
      label: isMobileDriver ? t("locationTransport.onTrip") : t("locationTransport.online"),
      color: 'success' as const,
    };
  }

  if (location.isOnline === false) {
    return {
      label: isMobileDriver ? t("locationTransport.offTrip") : t("locationTransport.offline"),
      color: 'default' as const,
    };
  }

  return { label: t("locationTransport.noStatus"), color: 'default' as const };
}

export function getTelemetryPresentation(status: LocationTelemetryStatus) {
  switch (status) {
    case 'recent':
      return { label: t("locationTransport.locationRecent"), color: 'success' as const };
    case 'delayed':
      return { label: t("locationTransport.locationDelayed"), color: 'warning' as const };
    case 'stale':
      return { label: t("locationTransport.locationStale"), color: 'error' as const };
    default:
      return { label: t("locationTransport.noLocation"), color: 'default' as const };
  }
}

export function getCompactTelemetryLabel(status: LocationTelemetryStatus) {
  switch (status) {
    case 'recent':
      return t("locationTransport.gpsRecent");
    case 'delayed':
      return t("locationTransport.gpsDelayed");
    case 'stale':
      return t("locationTransport.gpsStale");
    default:
      return t("locationTransport.noGps");
  }
}
